import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, SignOut, BookOpen, Megaphone, Receipt, UsersThree, UserCircle } from "@phosphor-icons/react";
import { ViewToggle } from "@/components/ViewToggle";
import NotificationsBell from "@/components/NotificationsBell";
import { Sidebar } from "@/components/Sidebar";
import BereavementFeed from "./components/BereavementFeed";
import BereavementDetail from "./components/BereavementDetail";
import MemberStatement from "./components/MemberStatement";
import MemberDirectory from "./components/MemberDirectory";
import AnnouncementsFeed from "./components/AnnouncementsFeed";
import ProfileView from "./components/ProfileView";

const tabs = [
  { key: "statement", icon: Receipt, label: "Statement" },
  { key: "feed", icon: BookOpen, label: "Bereavements" },
  { key: "announcements", icon: Megaphone, label: "Notices" },
  { key: "directory", icon: UsersThree, label: "Directory" },
  { key: "profile", icon: UserCircle, label: "Profile" },
];

export default function MemberDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("statement");
  const [selectedBereavement, setSelectedBereavement] = useState(null);

  if (selectedBereavement) {
    return <BereavementDetail bereavementId={selectedBereavement} onBack={() => setSelectedBereavement(null)} />;
  }

  return (
    <div className="min-h-screen bg-background lg:flex" data-testid="member-dashboard">
      <Sidebar items={tabs} activeKey={tab} onSelect={setTab} accent="primary" title="Member" />

      <div className="flex-1 flex flex-col lg:pl-64">
        {/* Mobile header */}
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl backdrop-saturate-150 border-b border-border px-4 py-3 flex items-center justify-between lg:hidden">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
              <Heart className="w-3.5 h-3.5 text-primary-foreground" weight="fill" />
            </div>
            <span className="font-heading text-lg font-bold tracking-tighter">Utheri</span>
          </div>
          <div className="flex items-center gap-2">
            {user?.memberNumber && (
              <span className="text-xs font-mono bg-secondary text-secondary-foreground px-2 py-1 rounded-lg hidden sm:block" data-testid="member-number-badge">
                {user.memberNumber}
              </span>
            )}
            <NotificationsBell />
            <ViewToggle />
            <button onClick={logout} className="p-1.5 rounded-lg hover:bg-secondary transition-colors" data-testid="logout-btn">
              <SignOut className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </header>

        {/* Desktop top bar */}
        <header className="hidden lg:flex sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-8 py-3 items-center justify-between">
          <h1 className="font-heading text-xl font-semibold">{tabs.find(t => t.key === tab)?.label || ""}</h1>
          <div className="flex items-center gap-2">
            <NotificationsBell />
            <ViewToggle />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 max-w-2xl lg:max-w-5xl w-full mx-auto pb-24 lg:pb-8 px-4 lg:px-8 py-6">
          {tab === "feed" && <BereavementFeed onSelect={setSelectedBereavement} />}
          {tab === "announcements" && <AnnouncementsFeed />}
          {tab === "statement" && <MemberStatement onSelectBereavement={setSelectedBereavement} />}
          {tab === "directory" && <MemberDirectory />}
          {tab === "profile" && <ProfileView />}
        </main>

        {/* Bottom Nav (mobile only) */}
        <nav className="fixed bottom-0 left-0 right-0 border-t border-border bg-background/80 backdrop-blur-xl flex justify-around items-center py-2 z-40 safe-area-bottom lg:hidden" data-testid="bottom-nav">
          {tabs.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`nav-${key}`}
              className={`flex flex-col items-center gap-0.5 flex-1 min-w-0 py-1 transition-colors ${tab === key ? "text-primary" : "text-muted-foreground"}`}
            >
              <Icon className="w-5 h-5 shrink-0" weight={tab === key ? "fill" : "regular"} />
              <span className="text-[10px] font-medium leading-tight truncate w-full text-center px-0.5">{label}</span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
